/*
 *  Product Thumbnail Print
 *    Exporting common product thumbnail string.
 *
 *
 *  Version:
 *    1.0.0 - 2022/06/30
 */

import { Currency, pictureGenerate, printMoney, t, Product, Variant, CurrencyFormat, VariantImage } from '@process-creative/slate-theme-tools';


type OptionWithValue = {
  name: string;
  values: string[];
  selected_value: string;
  position: 1 | 2 | 3;
}

type UspTag = {
  value: string;
};

type ProductObjectUpdated = Product & {
  options_with_values: OptionWithValue[];
  excerpt?: string | null;
  available: boolean;
  url: string;
  badge?: string | null;
  rollover_image?: Array<VariantImage> | null;
  rollover_video?: string | null;
  bisButtonLabel?: string;
  top_benefit_tags?: string;
  stars?: any;
  custom_badge?: string;
  custom_badge_invert?: boolean;
  product_colour?: string;
  usp_tags?: UspTag[];
}

/**
 * The parameters for the product thumbnail print functionality
 */
export type ProductThumbnailPrintParams = {
  p: ProductObjectUpdated;
  p2: ProductObjectUpdated;
  v: Variant;
  clazz: string;
  is_swatches: boolean | string;
}


/**
 * Creates a product thumbnail based on provided product object data
 * @param params The information necessary to display the product thumbnail
 * @returns string of html to be renedered
 */
export const ProductThumbnailPrint = (params: ProductThumbnailPrintParams) => {
  const { p, p2, v, clazz } = params;
  const shopURL =  window.Shopify?.shop || window.location.hostname;

  const win = window as Window & typeof globalThis &
  { Currency: { currency: Currency } };
  const currency: Currency = win.Currency.currency || 'AUD';
  const format: CurrencyFormat = 'money_format';

  function htmlDecode(input:any) {
    var doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent;
  }
  
  return /*html*/`
    <div 
      class="${clazz || ''}"
      data-product-thumbnail
      data-product-id="${p.id}"
      data-variant-id="${v.id}"
      data-product-handle="${p.handle}"
    >
      <div
        class="o-product-thumbnail ${p.rollover_image ? 'has-hover' : ''}"
      >
        <div class="o-product-thumbnail__inner">
          <div class="o-product-thumbnail__top-wrapper">
            ${swymBtn()}
            ${badge()}
            ${quickView()}
            <a href="${p.url}" class="o-product-thumbnail__media-wrapper">
              ${image(p.featured_image || v.featured_image || '', true)}
              
              ${image(p.hover_image || '', false)}
            </a>
          </div>
          <div class="o-product-thumbnail__content">
            ${title()}
            <div class="o-product-thumbnail__price-and-colour">
              ${productColour()}
              ${pricing()}
            </div>
            ${uspTags()}
          
          </div>
    
        </div>
      </div>
    </div>
  `;

  //product image
  function image(featuredImage, isPrimary) {
    if(!featuredImage ){
      return /* html */`` 
    }
    if (featuredImage.media_type == "video") {
      let videoSrc = "";

      for (let i = featuredImage?.sources.length - 1; i >= 0; i--) {
        const source = featuredImage?.sources[i];
        if (source.format === "mp4") {
          videoSrc = source.url;
          break;
        }
      }

      if (!videoSrc) {
        console.log("No mp4 video source found");
      } else {
        return `
          <video
            autoplay="autoplay"
            playsinline
            loop
            muted="muted"
            class="o-product-thumbnail__video o-media ${
              isPrimary ? "is-primary" : "is-secondary"
            }"
            poster="${p.image}"
          >

            <source src="${videoSrc}" type="video/mp4">
          </video>
          `;
      }
    } else if (featuredImage.media_type == "image") {
      return `
        <picture>
          <img src="${ featuredImage.src }" class="o-product-thumbnail__image o-media ${ isPrimary ? "is-primary" : "is-secondary" }" alt=""/>
        </picture>
      `;
    }
  }

  function swymBtn() {
    return `
      <button 
          type="button"
          class="o-product-thumbnail__wishlist-btn o-wishlist__wishlist-button swym-add-to-wishlist-view-product"
          data-wishlist-button
          data-product="${p.id}"
          data-variant="${(p as any).variants?.[0]?.id || v.id}"
          data-url="${p.url}"
        >
          <span class="u-visually-hidden">
            Wishlist
          </span>
          <svg class="o-wishlist__wishlist-icon is-inactive" viewBox="0 0 24 24">
            <path d="M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5 2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z" fill="none" stroke="currentColor" stroke-width="1.5"/>
          </svg>
      </button>
    `
  }

 
  //product title
  function title() {
    return /* html */ `
      <a href="${p.url}" class="o-product-thumbnail__title o-heading--6">
        ${p.title}
      </a>
    `
  }

  //product quickView
  function quickView() {
    return /* html */ `
      <button type="button" class="o-btn is-tertiary o-product-thumbnail__quick-view-btn" data-product-quick-view-btn>
        <span class="o-product-thumbnail__quick-view-btn-text o-paragraph--2">
          ${t('products.product.quick_view_label')} 
        </span>
        <svg class="o-product-thumbnail__quick-view-icon" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12"><path d="M1.875 6h8.25" stroke="currentColor" stroke-linecap="square"/><path d="M6 1.875v8.25" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/></svg>
      </button>
    `
  }

  function productColour(){
    if(!p.product_colour){
      return '';
    } else {
      return `
      <span class="o-product-thumbnail__colour o-paragraph--2">
        ${p.product_colour}
      </span>
  
      <span class="o-product-thumbnail__dot-divider"></span>
      `;
    }
  }
  
  function uspTags() {
    if (!Array.isArray(p.usp_tags) || p.usp_tags.length === 0) {
      return ''; // return nothing if no usp_tags
    }
    return /* html */ `
      <ul class="o-product-thumbnail__usp-list">
        ${p.usp_tags
          .map(uspTag => `
            <li class="o-product-thumbnail__usp-item o-paragraph--3" data-usp="${uspTag.value}">
              <span class="o-product-thumbnail__usp-badge">
                ${uspTag.value}
              </span>
            </li>
          `)
          .join('')}
      </ul>
    `;
  }

  function badge() {
    if (!Array.isArray(p.tags) || p.tags.length === 0) {
      return ''; // return nothing if no usp_tags
    }
    const badgeTags = p.tags
      .filter(tag => tag.startsWith('badge:'))
      .map(tag => tag.replace('badge:', ''));

    if (badgeTags.length === 0) {
      return ''; // return nothing if no badge tags
    }
    return /* html */ `
    ${badgeTags
      .map(label => `
        <div
          class="o-btn o-product-thumbnail__badge is-badge"
          aria-label="${label}">
          <span>${label}</span>
        </div>`)
      .join('')}
    `;
  }

  //product pricing
  function pricing() {
    const price = v.price;
    const comparePrice = v.compare_at_price ? v.compare_at_price : price;

    return /* html */`
    <p
      class="o-pricing
      o-product-thumbnail__pricing 
      ${comparePrice > price ? `has-compare` : ``}"
      data-product-pricing
    >
      ${comparePrice > price ? /* html */ `
      <span
        class="o-pricing__money o-pricing__compare 
        o-product-thumbnail__pricing-money 
        o-product-thumbnail__pricing-compare"
        data-currency
        data-money="${comparePrice}"
        data-product-compare-money>
          ${printMoney(comparePrice, format, currency)}
      </span>
      
      ` : ''}
      ${price == 0 ? /* html */`
        <span
          class="o-pricing__money o-pricing__price o-product-thumbnail__pricing-money
          o-product-thumbnail__pricing-price"
          data-currency
          data-product-money
        >
          ${t('products.product.free_price_label')}
        </span>`
        : /* html */ `
        <span 
          class="o-pricing__money o-pricing__price o-product-thumbnail__pricing-money
          o-product-thumbnail__pricing-price
          money
          ${comparePrice > price ? `has-compare` : ``}"
          data-currency
          data-money="${price}"
          data-product-money
          data-product-id="${p.id}"
        >
          ${printMoney(price, format, currency)}
        </span>`
      }
    </p>`
  }

};